import React from "react";

/**
 * Diagrams for /insights articles, referenced by name from articles.ts.
 *
 * SVGs use a 640-wide viewBox with large type so they stay legible when
 * scaled down to a phone-width column. Colors are the marketing tokens:
 * ink #17150F, green #1E4D38, mint #E4EFE4, line #E4DFD3, rust #A4462B.
 */

const INK = "#17150F";
const GREEN = "#1E4D38";
const MINT = "#E4EFE4";
const LINE = "#CFC7B5";
const RUST = "#A4462B";
const BLUSH = "#F6E3DC";
const PAPER = "#FFFDF8";
const FONT = "Inter, Arial, sans-serif";

function Arrowheads() {
  return (
    <defs>
      {[
        ["ah-ink", INK],
        ["ah-rust", RUST],
        ["ah-green", GREEN],
      ].map(([id, c]) => (
        <marker key={id} id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill={c} />
        </marker>
      ))}
    </defs>
  );
}

/** Multi-line text helper: lines centered on x, starting at y. */
function Lines({ x, y, lines, size = 17, weight = 400, fill = INK, anchor = "middle" }: {
  x: number; y: number; lines: string[]; size?: number; weight?: number; fill?: string; anchor?: "middle" | "start";
}) {
  return (
    <text x={x} y={y} fontFamily={FONT} fontSize={size} fontWeight={weight} fill={fill} textAnchor={anchor}>
      {lines.map((l, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : size * 1.3}>
          {l}
        </tspan>
      ))}
    </text>
  );
}

function Handshake() {
  const cx = 110, sx = 530;
  return (
    <svg viewBox="0 0 640 430" role="img" aria-labelledby="hs-t" className="w-full">
      <title id="hs-t">How a TCP connection starts: SYN with IP options, SYN-ACK, ACK, then the new socket takes over the saved options</title>
      <Arrowheads />
      <Lines x={cx} y={34} lines={["Client"]} size={19} weight={600} />
      <Lines x={sx} y={34} lines={["Server"]} size={19} weight={600} />
      <line x1={cx} y1={50} x2={cx} y2={420} stroke={LINE} strokeWidth={2} strokeDasharray="4 6" />
      <line x1={sx} y1={50} x2={sx} y2={420} stroke={LINE} strokeWidth={2} strokeDasharray="4 6" />

      <Lines x={320} y={86} lines={["1  SYN (can carry IP options)"]} size={17} weight={600} />
      <line x1={cx} y1={100} x2={sx - 4} y2={100} stroke={INK} strokeWidth={2.5} markerEnd="url(#ah-ink)" />
      <rect x={300} y={118} width={330} height={52} rx={12} fill={MINT} />
      <Lines x={465} y={150} lines={["Kernel saves the options"]} size={17} fill={GREEN} weight={600} />

      <Lines x={320} y={212} lines={["2  SYN-ACK (built using the options)"]} size={17} weight={600} />
      <line x1={sx} y1={226} x2={cx + 4} y2={226} stroke={INK} strokeWidth={2.5} markerEnd="url(#ah-ink)" />

      <Lines x={320} y={280} lines={["3  ACK"]} size={17} weight={600} />
      <line x1={cx} y1={294} x2={sx - 4} y2={294} stroke={INK} strokeWidth={2.5} markerEnd="url(#ah-ink)" />
      <rect x={250} y={318} width={380} height={78} rx={12} fill={MINT} />
      <Lines x={440} y={350} lines={["New connection socket takes", "over the saved options"]} size={17} fill={GREEN} weight={600} />
    </svg>
  );
}

function LaneBox({ x, y, w = 280, h, tone, lines }: { x: number; y: number; w?: number; h: number; tone: "plain" | "bad" | "good"; lines: string[] }) {
  const fill = tone === "bad" ? BLUSH : tone === "good" ? MINT : PAPER;
  const stroke = tone === "bad" ? RUST : tone === "good" ? GREEN : LINE;
  const text = tone === "bad" ? RUST : tone === "good" ? GREEN : INK;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={12} fill={fill} stroke={stroke} strokeWidth={1.5} />
      <Lines x={x + w / 2} y={y + 30} lines={lines} size={16.5} fill={text} weight={tone === "plain" ? 400 : 600} />
    </g>
  );
}

function Lanes({ title, children, height }: { title: string; children: React.ReactNode; height: number }) {
  return (
    <svg viewBox={`0 0 640 ${height}`} role="img" aria-label={title} className="w-full">
      <title>{title}</title>
      <Arrowheads />
      <Lines x={170} y={30} lines={["CPU 1"]} size={19} weight={600} />
      <Lines x={470} y={30} lines={["CPU 2"]} size={19} weight={600} />
      <line x1={320} y1={46} x2={320} y2={height - 10} stroke={LINE} strokeWidth={1.5} strokeDasharray="4 6" />
      {children}
    </svg>
  );
}

function Race() {
  return (
    <Lanes title="The race: CPU 1 frees the options immediately while CPU 2 is still about to read them" height={500}>
      <LaneBox x={30} y={56} h={84} tone="plain" lines={["Handshake completes.", "New socket now owns", "the options"]} />
      <LaneBox x={330} y={150} h={84} tone="plain" lines={["A retransmitted SYN", "arrives. Starts building", "another SYN-ACK"]} />
      <LaneBox x={30} y={244} h={84} tone="bad" lines={["Connection closes.", "Options freed at once", "(plain kfree)"]} />
      <LaneBox x={330} y={338} h={84} tone="bad" lines={["Reads the options to", "finish the SYN-ACK.", "The memory is gone"]} />
      <path d="M310,300 C330,320 330,340 336,360" fill="none" stroke={RUST} strokeWidth={2.5} strokeDasharray="6 5" markerEnd="url(#ah-rust)" />
      <rect x={180} y={440} width={280} height={44} rx={22} fill={RUST} />
      <Lines x={320} y={468} lines={["Use-after-free"]} size={18} fill="#FBF8F1" weight={700} />
    </Lanes>
  );
}

function Fix() {
  return (
    <Lanes title="The fix: the free waits for an RCU grace period, so CPU 2 finishes reading before the memory is released" height={500}>
      <LaneBox x={30} y={56} h={84} tone="plain" lines={["Handshake completes.", "New socket now owns", "the options"]} />
      <LaneBox x={330} y={150} h={84} tone="plain" lines={["A retransmitted SYN", "arrives. Starts building", "another SYN-ACK"]} />
      <LaneBox x={30} y={244} h={84} tone="good" lines={["Connection closes.", "Free is scheduled", "(kfree_rcu)"]} />
      <LaneBox x={330} y={338} h={84} tone="good" lines={["Reads the options.", "They are still valid.", "SYN-ACK goes out"]} />
      <LaneBox x={30} y={410} h={74} tone="plain" lines={["Grace period ends.", "Now the memory is freed"]} />
      <path d="M180,328 L180,404" fill="none" stroke={GREEN} strokeWidth={2.5} strokeDasharray="6 5" markerEnd="url(#ah-green)" />
    </Lanes>
  );
}

/** Decision flow, in HTML so it reflows on narrow screens. */
function Decide() {
  const Step = ({ q, children }: { q: string; children: React.ReactNode }) => (
    <div className="rounded-2xl border border-[#E4DFD3] bg-white p-5">
      <p className="font-semibold">{q}</p>
      <div className="mt-3 grid gap-2 text-[0.95rem] leading-relaxed sm:grid-cols-2">{children}</div>
    </div>
  );
  const Ans = ({ tone, label, children }: { tone: "good" | "bad" | "next"; label: string; children: React.ReactNode }) => (
    <div className={`rounded-xl px-4 py-3 ${tone === "good" ? "bg-[#E4EFE4] text-[#1E4D38]" : tone === "bad" ? "bg-[#F6E3DC] text-[#7A3320]" : "bg-[#F4EFE3]"}`}>
      <span className="font-mono text-xs font-semibold uppercase tracking-wider">{label}</span>
      <p className="mt-1">{children}</p>
    </div>
  );
  const Down = () => <div className="mx-auto h-6 w-px bg-[#CFC7B5]" aria-hidden />;
  return (
    <div className="not-prose">
      <Step q="1. Is the host running a kernel with the fix?">
        <Ans tone="good" label="Yes">You are done. Confirm every host, including container hosts and appliances.</Ans>
        <Ans tone="next" label="No or not sure">Go to step 2.</Ans>
      </Step>
      <Down />
      <Step q="2. Does it accept IPv4 TCP connections from networks you don't fully trust?">
        <Ans tone="bad" label="Yes">Patch first. Internet-facing servers and load balancers go to the top of the list.</Ans>
        <Ans tone="next" label="No">Still patch, on your normal emergency cycle. Internal attackers count too.</Ans>
      </Step>
      <Down />
      <Step q="3. Can't reboot yet? Reduce exposure while you wait">
        <Ans tone="next" label="Network">Drop incoming IPv4 TCP packets that carry IP options. Normal traffic rarely uses them.</Ans>
        <Ans tone="next" label="Host">Turn off MPTCP if you don't use it, and consider booting with init_on_free=1.</Ans>
      </Step>
      <Down />
      <div className="rounded-2xl bg-[#1E4D38] p-5 text-center font-semibold text-[#FBF8F1]">
        Install the fixed kernel and reboot. Mitigations buy time; only the fix closes the bug.
      </div>
    </div>
  );
}

const FIGURES: Record<string, React.FC> = {
  handshake: Handshake,
  race: Race,
  fix: Fix,
  decide: Decide,
};

export function ArticleFigure({ name, caption }: { name: string; caption?: string }) {
  const Fig = FIGURES[name];
  if (!Fig) return null;
  return (
    <figure className="mt-8">
      <div className="rounded-2xl border border-[#E4DFD3] bg-[#FFFDF8] p-4 sm:p-6">
        <Fig />
      </div>
      {caption && <figcaption className="mt-3 text-sm leading-relaxed opacity-60">{caption}</figcaption>}
    </figure>
  );
}
